import { useState } from "react";
import { X, Sparkles, Loader2, FileText, Send, Check } from "lucide-react";
import { axiosInstance } from "../../lib/axios";
import toast from "react-hot-toast";

interface Summary {
  discussion: string; decisions: string[]; actionItems: string[]; responsibilities: string[]; nextSteps: string[];
}

// AI Meeting Minutes — BD pastes notes, AI refines into a professional summary,
// optionally emailed to the client.
export const MeetingMinutesModal = ({ meetingId, meetingTitle, onClose }: { meetingId: string; meetingTitle?: string; onClose: () => void }) => {
  const [notes, setNotes] = useState("");
  const [sendToClient, setSendToClient] = useState(true);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [emailed, setEmailed] = useState(false);

  const submit = async () => {
    if (!notes.trim()) return toast.error("Add your meeting notes first.");
    setBusy(true);
    try {
      const { data } = await axiosInstance.post(`/activities/${meetingId}/minutes`, { notes, send: sendToClient });
      setSummary(data.summary);
      setEmailed(!!data.emailed);
      toast.success(data.aiGenerated ? "Minutes refined with AI" : "Minutes saved");
      if (sendToClient && !data.hasClientEmail) toast("No client email on file — summary saved but not sent.", { icon: "✉️" });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't save minutes");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-outline-variant flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-blue-50 grid place-items-center text-primary"><FileText size={18} /></div>
            <div>
              <h2 className="text-[17px] font-bold text-on-surface leading-tight">Meeting Minutes</h2>
              <p className="text-[12px] text-on-surface-variant">{meetingTitle || "Share what was discussed"}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-surface-container-low text-on-surface-variant"><X size={18} /></button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4">
          {!summary ? (
            <>
              <div>
                <label className="text-[12px] font-semibold text-on-surface mb-1.5 block">Your notes</label>
                <textarea rows={6} value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Jot down what was discussed, decisions, action items… AI will organise and format it professionally."
                  className="w-full border border-outline-variant rounded-lg p-3 text-[13px] focus:ring-2 focus:ring-primary outline-none" />
              </div>
              <label className="flex items-center gap-2.5 text-[13px] text-on-surface cursor-pointer">
                <input type="checkbox" checked={sendToClient} onChange={(e) => setSendToClient(e.target.checked)} className="w-4 h-4 accent-[#0058c3]" />
                Email the finalized summary to the client (if an email is on file)
              </label>
            </>
          ) : (
            <div className="space-y-4">
              {emailed && (
                <div className="flex items-center gap-2 text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  <Check size={14} /> Summary emailed to the client.
                </div>
              )}
              <Block title="Discussion"><p className="text-[13px] text-on-surface-variant leading-relaxed">{summary.discussion}</p></Block>
              <ListBlock title="Decisions" items={summary.decisions} />
              <ListBlock title="Action Items" items={summary.actionItems} />
              <ListBlock title="Responsibilities" items={summary.responsibilities} />
              <ListBlock title="Next Steps" items={summary.nextSteps} />
            </div>
          )}
        </div>

        <div className="p-4 border-t border-outline-variant bg-surface-container-low/50 flex justify-end gap-2">
          {!summary ? (
            <button onClick={submit} disabled={busy} className="px-5 py-2 rounded-lg bg-primary text-white text-[13px] font-semibold hover:bg-[#004b9e] inline-flex items-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : sendToClient ? <Send size={15} /> : <Sparkles size={15} />}
              {sendToClient ? "Refine & Send" : "Refine & Save"}
            </button>
          ) : (
            <button onClick={onClose} className="px-5 py-2 rounded-lg bg-primary text-white text-[13px] font-semibold hover:bg-[#004b9e]">Done</button>
          )}
        </div>
      </div>
    </div>
  );
};

const Block = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div><h4 className="text-[12px] font-bold text-on-surface mb-1">{title}</h4>{children}</div>
);
const ListBlock = ({ title, items }: { title: string; items: string[] }) => (
  <div>
    <h4 className="text-[12px] font-bold text-on-surface mb-1.5">{title}</h4>
    {items && items.length ? (
      <ul className="space-y-1">{items.map((it, i) => (
        <li key={i} className="text-[13px] text-on-surface-variant flex items-start gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" /> {it}</li>
      ))}</ul>
    ) : <p className="text-[13px] text-outline">—</p>}
  </div>
);
